import { useContext, useEffect, useState } from "react"
import classNames from "../../../utils/classNames"
import SearchBar from "../SearchBar"
import SearchDropDown from "../SearchDropDown"
import { UserProps } from "../../../interfaces"
import api from "../../../api2"
import AdminDropdown from "./AdminDropdown"
import ADMIN_NAVBAR_ITEMS from "../../../constants/admin_navbar_items"
import { AdminContext } from "../../../contexts/AdminContext"
import * as Sentry from "@sentry/react"
import fetchParkLogoSrc from "./fetchParkLogoSrc"
import eAdminUserRole from "~/enums/eAdminUserRole"
import { useMe } from "~/api2/hooks/useMe"
import { useLocation, useNavigate } from "react-router-dom"

const Navbar = () => {
  const { currentPage, setCurrentPage } = useContext(AdminContext)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const user = useMe()

  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchResults, setSearchResults] = useState<UserProps[]>(null)
  const [searchStatus, setSearchStatus] = useState<any>({})
  const [matchedRoute, setMatchedRoute] = useState<string>("reservations")

  const currentUserRole = user?.data?.roles?.[0]

  const handleLogout = () => {
    api.Sessions.logout()
      .then((data) => {
        console.log("logged out, moving to login page", data)
        window.location.href = "/admin/login"
      })
      .catch((err) => {
        console.error("Error logging out:", err)
        Sentry.captureException("Error logging out user on FE", { contexts: { info: { errorMessage: err } } })
      })
  }

  useEffect(() => {
    const matched = ADMIN_NAVBAR_ITEMS.find(
      (item) =>
        item.routes?.some((route) => {
          return pathname.includes(route)
        }) || item.route === pathname,
    )

    if (matched && matched.key !== currentPage) {
      setMatchedRoute(matched.key)
    }
  }, [pathname, currentPage, setMatchedRoute])

  return (
    <>
      <div
        className="fixed top-0 z-20 bg-white border-b flex flex-col justify-center w-full max-h-24"
        data-testid="nav-bar"
      >
        <>
          <div className="flex-grow mx-4 lg:mx-12 xl:mx-24 2xl:mx-36">
            <div className="relative flex items-center justify-between h-20">
              <div className="flex items-center lg:px-0">
                <div className="flex-shrink-0">
                  <img
                    data-testid="nav-bar-logo"
                    src={fetchParkLogoSrc()}
                    className="block z-10 h-8 lg:h-10 w-auto cursor-pointer"
                    onClick={() => {
                      if (window.location.pathname === "/admin/reservations") {
                        setCurrentPage("reservations")
                      } else {
                        localStorage.setItem("toNav", "reservations")
                        window.location.href = "/admin/reservations"
                      }
                    }}
                  />
                </div>
                <div className="hidden lg:block lg:ml-8">
                  <div className="flex space-x-4">
                    {ADMIN_NAVBAR_ITEMS.map((item) => {
                      const isCurrent = currentPage ? currentPage == item.key : matchedRoute == item.key
                      if (
                        !(currentUserRole == "user" && item.key == "reports") &&
                        !(currentUserRole == "view-only" && (item.key == "reports" || item.key == "invoices"))
                      ) {
                        if (
                          (item.key === "settings" &&
                            ![eAdminUserRole.ParkAdmin, eAdminUserRole.Admin].includes(currentUserRole)) ||
                          (item.key === "reports" &&
                            ![eAdminUserRole.ParkAdmin, eAdminUserRole.Admin, eAdminUserRole.Manager].includes(
                              currentUserRole,
                            )) ||
                          (currentUserRole === eAdminUserRole.ViewOnly &&
                            (item.key === "reports" || item.key === "invoices" || item.key == "store"))
                        ) {
                          return null
                        }
                        return (
                          <a
                            data-testid={`nav-bar-routes-${item.name}`}
                            key={item.name}
                            onMouseDown={(e) => {
                              e.preventDefault()
                              e.currentTarget.blur()
                              const isMiddleClick = e.button === 1
                              const isCtrlClick = e.metaKey || e.ctrlKey

                              const targetRoute = item.route || item.href

                              if (isMiddleClick || isCtrlClick) {
                                const newTab = window.open(targetRoute, "_blank")
                                if (item.key) {
                                  localStorage.setItem("toNav", item.key)
                                }
                                localStorage.setItem("from", window.location.pathname)

                                if (newTab) newTab.focus()
                              } else {
                                console.debug("[ROUTES] Clicked on:", item.name, "Route:", item.route)
                                if (item.route) {
                                  if (!window.location.pathname.startsWith("/admin/reservations")) {
                                    if (typeof navigate !== "function") {
                                      console.warn(
                                        "[ROUTES] Navigate function is not available, using window.location.href for:",
                                        item.route,
                                      )
                                      window.location.href = item.route
                                    } else {
                                      console.debug("[ROUTES] Navigating to:", item.route)
                                      navigate(item.route)
                                    }
                                  } else {
                                    console.debug("[ROUTES] Already on reservations page, navigating to:", item.route)
                                    window.location.href = item.route
                                  }
                                }
                                if (item.key && item.route === undefined) {
                                  setCurrentPage(item.key)
                                  localStorage.setItem("toNav", item.key)
                                }
                                localStorage.setItem("from", window.location.pathname)

                                if (window.location.pathname !== "/admin/reservations" && !item.route) {
                                  console.debug("[ROUTES] Redirecting to /admin/reservations")
                                  window.location.href = "/admin/reservations"
                                }
                              }
                            }}
                            className={classNames(
                              isCurrent
                                ? "bg-gray-100 text-gray-900 hover:text-gray-900"
                                : "text-gray-900 hover:bg-gray-100 hover:text-gray-900 cursor-pointer",
                              "px-3 py-2.5 rounded-md text-sm ",
                            )}
                          >
                            {item.name}
                          </a>
                        )
                      }
                    })}
                  </div>
                </div>
              </div>
              <div className="flex-1 flex justify-center px-4 sm:ml-6 sm:justify-end">
                <SearchBar
                  showResults={() => {
                    if (!showSearchResults) setShowSearchResults(true)
                  }}
                  onBlur={(value: string) => {
                    if (!value) setSearchStatus({})
                    setShowSearchResults(false)
                  }}
                  setSearchResults={setSearchResults}
                  setSearchStatus={setSearchStatus}
                  visible={showSearchResults}
                />
              </div>
              <AdminDropdown handleLogout={handleLogout} />
            </div>
          </div>
        </>
      </div>

      <SearchDropDown visible={showSearchResults} searchResults={searchResults} searchStatus={searchStatus} />
    </>
  )
}

export default Navbar
